import React, { useState, useEffect, useMemo } from 'react';
import { speciesAPI } from '../../services/api';
import { toast } from 'react-toastify';

const PHOTO_BASE = window.location.origin;
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${PHOTO_BASE}${url}`;
};

/**
 * Displays the Plant Species allocated to a project, grouped by zone/plot.
 * Optional: adminMode enables inline add/edit/delete of allocations.
 *
 * Props:
 *   targetId      - required, plantation_target id
 *   adminMode     - if true, shows admin CRUD controls (default false)
 */
export default function ProjectSpeciesSection({ targetId, adminMode = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSpecies, setExpandedSpecies] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [speciesCatalog, setSpeciesCatalog] = useState([]);
  const [addForm, setAddForm] = useState({ species_id: '', zone_label: '', quantity: '' });

  const load = () => {
    setLoading(true);
    speciesAPI.targetSpecies(targetId)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(load, [targetId]);

  useEffect(() => {
    if (adminMode) {
      speciesAPI.list().then(res => setSpeciesCatalog(res.data.items || [])).catch(() => {});
    }
  }, [adminMode]);

  const zones = data?.zones || [];
  const speciesTotals = data?.species_totals || [];
  const grandTotal = data?.grand_total || 0;
  const uniqueSpecies = data?.unique_species || 0;

  const availableZoneLabels = useMemo(() => {
    const set = new Set(zones.map(z => z.zone_label));
    return Array.from(set);
  }, [zones]);

  const handleAdd = async () => {
    if (!addForm.species_id || !addForm.quantity) {
      toast.warn('Please pick species and enter quantity');
      return;
    }
    try {
      await speciesAPI.addAllocation(targetId, {
        species_id: parseInt(addForm.species_id),
        zone_label: addForm.zone_label || null,
        quantity: parseInt(addForm.quantity),
      });
      toast.success('Allocation added');
      setShowAdd(false);
      setAddForm({ species_id: '', zone_label: '', quantity: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add');
    }
  };

  const handleSaveEdit = async (row) => {
    try {
      await speciesAPI.updateAllocation(row.id, {
        quantity: parseInt(row.quantity),
        zone_label: row.zone_label,
        notes: row.notes,
      });
      toast.success('Saved');
      setEditingRow(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
  };

  const handleDelete = async (allocId) => {
    if (!window.confirm('Delete this allocation?')) return;
    try {
      await speciesAPI.deleteAllocation(allocId);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, marginBottom: 24, textAlign: 'center', color: '#9ca3af' }}>
        Loading species...
      </div>
    );
  }

  if (!data || zones.length === 0) {
    return (
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, marginBottom: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a2e', marginBottom: 12 }}>
          🌿 Plant Species
        </div>
        <div style={{ color: '#9ca3af', fontSize: 14 }}>
          No species allocated to this project yet.
          {adminMode && (
            <button onClick={() => setShowAdd(true)} style={{ marginLeft: 12, padding: '4px 12px', background: '#1a7431', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              + Add Species
            </button>
          )}
        </div>
        {adminMode && showAdd && <AddDialog {...{ speciesCatalog, availableZoneLabels, addForm, setAddForm, onSave: handleAdd, onClose: () => setShowAdd(false) }} />}
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 24, marginBottom: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
          🌿 Plant Species &nbsp;
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
            {uniqueSpecies} species · {grandTotal.toLocaleString()} plants across {zones.length} zone{zones.length !== 1 ? 's' : ''}
          </span>
        </div>
        {adminMode && (
          <button onClick={() => setShowAdd(true)} style={{ padding: '8px 14px', background: '#1a7431', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            + Add Species
          </button>
        )}
      </div>

      {/* Top species summary strip */}
      {speciesTotals.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Top species by count:</div>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
            {speciesTotals.slice(0, 8).map(s => (
              <div key={s.id}
                onClick={() => setExpandedSpecies(expandedSpecies === s.id ? null : s.id)}
                style={{
                  flexShrink: 0, width: 130, background: '#f0fdf4', borderRadius: 10, padding: 8,
                  border: expandedSpecies === s.id ? '2px solid #16a34a' : '1px solid #d1fae5',
                  cursor: 'pointer', textAlign: 'center',
                }}>
                {s.image_url ? (
                  <img src={getPhotoUrl(s.image_url)} alt={s.common_name}
                    style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 6 }}
                    onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div style={{ width: '100%', height: 80, background: '#e5e7eb', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🌱</div>
                )}
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1a7431', marginTop: 4, lineHeight: 1.2 }}>{s.common_name}</div>
                {s.local_name && <div style={{ fontSize: 10, color: '#6b7280' }}>{s.local_name}</div>}
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginTop: 3 }}>{s.total_quantity.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-zone breakdown */}
      {zones.map(z => (
        <div key={z.zone_label} style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1b5e20' }}>📍 {z.zone_label}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{z.items.length} species · {z.total.toLocaleString()} plants</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'left', color: '#6b7280', fontSize: 11, textTransform: 'uppercase' }}>
                  <th style={{ padding: '6px 8px' }}>Image</th>
                  <th style={{ padding: '6px 8px' }}>Common name</th>
                  <th style={{ padding: '6px 8px' }}>Local name</th>
                  <th style={{ padding: '6px 8px' }}>Scientific</th>
                  <th style={{ padding: '6px 8px' }}>Family</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Quantity</th>
                  {adminMode && <th style={{ padding: '6px 8px' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {z.items.map(row => {
                  const sp = row.species || {};
                  const isEditing = editingRow?.id === row.id;
                  return (
                    <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '6px 8px' }}>
                        {sp.image_url ? (
                          <img src={getPhotoUrl(sp.image_url)} alt={sp.common_name}
                            style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                        ) : (
                          <div style={{ width: 44, height: 44, background: '#e5e7eb', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🌱</div>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', fontWeight: 600, color: '#1a1a2e' }}>{sp.common_name}</td>
                      <td style={{ padding: '6px 8px', color: '#6b7280' }}>{sp.local_name || '—'}</td>
                      <td style={{ padding: '6px 8px', color: '#374151', fontStyle: 'italic' }}>{sp.scientific_name || '—'}</td>
                      <td style={{ padding: '6px 8px', color: '#6b7280' }}>{sp.family || '—'}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#1a7431' }}>
                        {isEditing ? (
                          <input type="number" value={editingRow.quantity} min="0"
                            onChange={e => setEditingRow({ ...editingRow, quantity: e.target.value })}
                            style={{ width: 80, padding: 4, textAlign: 'right', border: '1px solid #d1d5db', borderRadius: 4 }} />
                        ) : row.quantity.toLocaleString()}
                      </td>
                      {adminMode && (
                        <td style={{ padding: '6px 8px' }}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => handleSaveEdit(editingRow)} style={{ padding: '4px 8px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Save</button>
                              <button onClick={() => setEditingRow(null)} style={{ padding: '4px 8px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Cancel</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => setEditingRow({ ...row })} style={{ padding: '4px 8px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Edit</button>
                              <button onClick={() => handleDelete(row.id)} style={{ padding: '4px 8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Delete</button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {adminMode && showAdd && (
        <AddDialog {...{ speciesCatalog, availableZoneLabels, addForm, setAddForm, onSave: handleAdd, onClose: () => setShowAdd(false) }} />
      )}
    </div>
  );
}

function AddDialog({ speciesCatalog, availableZoneLabels, addForm, setAddForm, onSave, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 500, maxWidth: '90vw' }}>
        <h3 style={{ margin: '0 0 16px', color: '#1b5e20' }}>Add Species to Project</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Species *</label>
            <select value={addForm.species_id} onChange={e => setAddForm({ ...addForm, species_id: e.target.value })}
              style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}>
              <option value="">-- select species --</option>
              {speciesCatalog.map(s => (
                <option key={s.id} value={s.id}>{s.common_name}{s.scientific_name ? ` (${s.scientific_name})` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Zone / Plot label</label>
            <input list="zone-labels" value={addForm.zone_label} onChange={e => setAddForm({ ...addForm, zone_label: e.target.value })}
              placeholder="e.g. Zone 1 - BPTP corner, Plot 21"
              style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
            <datalist id="zone-labels">
              {availableZoneLabels.map(z => <option key={z} value={z} />)}
            </datalist>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Quantity *</label>
            <input type="number" min="1" value={addForm.quantity} onChange={e => setAddForm({ ...addForm, quantity: e.target.value })}
              style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 16px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
            <button onClick={onSave} style={{ padding: '8px 16px', background: '#1a7431', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Add</button>
          </div>
        </div>
      </div>
    </div>
  );
}
